import { useTranslation } from 'react-i18next';

import { queryKeys, useApiQuery } from '../../api/hooks';
import type { StockMoveResponse } from '../../api/types';
import { AsyncSection } from '../../components/AsyncSection';
import { PanelHeading } from '../money/PanelHeading';

/**
 * One item's history at one branch (FR-801).
 *
 * The balance is a number; this is the argument for it. Every row states what it became, so the
 * card can be read one page at a time rather than by summing forty movements — which is also what
 * makes a disagreement with the shelf a conversation about a specific Tuesday rather than about the
 * software.
 */
export function StockCard({
  clinicId,
  branchId,
  itemId,
}: {
  readonly clinicId: string;
  readonly branchId: string;
  readonly itemId: string;
}) {
  const { t } = useTranslation();
  const moves = useApiQuery<StockMoveResponse[]>(
    queryKeys.stockCard(clinicId, branchId, itemId),
    `/clinics/${clinicId}/inventory/stock/card?branchId=${branchId}&itemId=${itemId}`,
  );

  return (
    <AsyncSection query={moves}>
      {(rows) => (
        <section className="panel">
          <PanelHeading title={t('inventory.card')} hint={t('inventory.cardHint')} />
          {rows.length === 0 ? (
            <p className="muted">{t('inventory.nothingMoved')}</p>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('inventory.movedAt')}</th>
                    <th>{t('inventory.kind')}</th>
                    <th>{t('inventory.quantity')}</th>
                    <th>{t('inventory.balanceAfter')}</th>
                    <th>{t('inventory.reason')}</th>
                    <th>{t('inventory.movedBy')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((move) => (
                    <tr key={move.id}>
                      <td>{move.movedAt?.slice(0, 16).replace('T', ' ')}</td>
                      <td>
                        <span className={`pill pill--${(move.kind ?? '').toLowerCase()}`}>
                          {t(`inventory.moveKinds.${move.kind}`)}
                        </span>
                      </td>
                      <td>{move.quantity}</td>
                      <td>
                        <strong>{move.balanceAfter}</strong>
                      </td>
                      <td className="muted">{move.reason ?? move.supplier ?? '—'}</td>
                      <td className="muted">{move.movedByName ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </AsyncSection>
  );
}
